import { useState } from "react";
import { Lock, CheckCircle2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Brand } from "../components/Brand";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";

export default function Login() {
  const { login, commit } = useAuth();
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [welcome, setWelcome] = useState(null);

  const submit = async (value) => {
    setBusy(true);
    setError("");
    try {
      const user = await login(value);
      setWelcome(user.name);
      setTimeout(() => commit(user), 1400);
    } catch (err) {
      setError(formatApiError(err));
      setPin("");
      setBusy(false);
      setTimeout(() => document.querySelector('[data-testid="login-pin-input"]')?.focus(), 50);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-blue-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-15" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "28px 28px" }} />
        <div className="relative">
          <img src="/pos-finder-logo.svg" alt="POS Finder" className="h-24 w-24 rounded-3xl ring-4 ring-white/30 shadow-2xl shadow-blue-950/40" data-testid="login-hero-logo" />
          <p className="font-mono-label text-xs text-blue-100 mt-6">Private lead intelligence</p>
        </div>
        <div className="relative max-w-md">
          <h1 className="font-display font-extrabold text-5xl leading-[1.05] tracking-tight">
            Find businesses ready to ditch their POS.
          </h1>
        </div>
        <p className="relative text-sm text-blue-200">All 50 states · Updated daily</p>
      </div>

      <div className="flex flex-col items-center justify-center gap-6 p-6 bg-slate-50 lg:bg-white">
        <div className="w-full max-w-sm bg-white rounded-2xl border border-slate-200 p-8 shadow-xl shadow-slate-200/60 fade-up" data-testid="login-form">
          <Brand large />
          {welcome ? (
            <div className="mt-10 text-center fade-up" data-testid="login-welcome">
              <CheckCircle2 className="h-10 w-10 text-blue-700 mx-auto" />
              <p className="text-sm text-slate-500 mt-4">Welcome back,</p>
              <p className="font-display font-extrabold text-3xl text-slate-900 mt-1" data-testid="login-welcome-name">{welcome}</p>
            </div>
          ) : (
            <>
              <p className="mt-3 text-sm text-slate-500 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Team access only</p>
              <p className="mt-8 mb-3 font-mono-label text-[10px] text-slate-500">Enter your 4-digit PIN</p>
              <InputOTP maxLength={4} value={pin} onChange={setPin} onComplete={submit} disabled={busy}
                inputMode="numeric" pattern="^[0-9]*$" autoFocus data-testid="login-pin-input">
                <InputOTPGroup className="gap-3">
                  {[0, 1, 2, 3].map((i) => (
                    <InputOTPSlot key={i} index={i} className="h-16 w-16 text-2xl font-display font-bold rounded-xl border" />
                  ))}
                </InputOTPGroup>
              </InputOTP>
              {busy && <p className="text-sm text-slate-500 mt-4">Checking…</p>}
              {error && <p className="text-sm text-red-600 mt-4" data-testid="login-error">{error}</p>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
